import { useCart } from '@/hooks/useCart';
import { Link } from 'react-router-dom';

export default function CartDrawer() {
  const { items, removeItem, updateQuantity, totalItems, totalPrice, isOpen, setIsOpen } = useCart();

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-black/40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md bg-white shadow-2xl transition-transform duration-500 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#1C1C1C]/8">
          <div>
            <h2 className="font-serif text-xl font-bold text-[#1C1C1C]">კალათა</h2>
            <p className="text-xs text-[#1C1C1C]/40 mt-0.5">{totalItems} პროდუქტი</p>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-9 h-9 flex items-center justify-center text-[#1C1C1C]/40 hover:text-[#1C1C1C] transition-colors cursor-pointer"
            aria-label="დახურვა"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <i className="ri-shopping-bag-line text-4xl text-[#1C1C1C]/15 mb-4"></i>
              <p className="text-sm text-[#1C1C1C]/40 mb-1">კალათა ცარიელია</p>
              <p className="text-xs text-[#1C1C1C]/25 mb-6">დაამატეთ პროდუქტები მაღაზიიდან</p>
              <button
                onClick={() => setIsOpen(false)}
                className="text-sm font-semibold text-[#ec3145] hover:text-[#c9283a] transition-colors cursor-pointer whitespace-nowrap"
              >
                მაღაზიაში დაბრუნება
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex gap-4 p-3 bg-[#F5F2EE] border border-[#1C1C1C]/5"
                >
                  <Link
                    to={`/product/${item.id}`}
                    onClick={() => setIsOpen(false)}
                    className="w-20 h-20 flex-shrink-0 overflow-hidden cursor-pointer"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/product/${item.id}`}
                      onClick={() => setIsOpen(false)}
                      className="block"
                    >
                      <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#ec3145]">
                        {item.brand}
                      </span>
                      <h4 className="font-serif text-sm font-bold text-[#1C1C1C] mt-0.5 truncate">
                        {item.name}
                      </h4>
                    </Link>
                    <p className="text-xs text-[#1C1C1C]/30 mt-0.5">{item.category}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-[#1C1C1C]/10">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center text-[#1C1C1C]/40 hover:text-[#1C1C1C] transition-colors cursor-pointer"
                          aria-label="შემცირება"
                        >
                          <i className="ri-subtract-line text-xs"></i>
                        </button>
                        <span className="w-7 h-7 flex items-center justify-center text-xs font-semibold text-[#1C1C1C]">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center text-[#1C1C1C]/40 hover:text-[#1C1C1C] transition-colors cursor-pointer"
                          aria-label="გაზრდა"
                        >
                          <i className="ri-add-line text-xs"></i>
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-serif text-sm font-bold text-[#1C1C1C]">
                          ₾{item.price * item.quantity}
                        </span>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-[#1C1C1C]/20 hover:text-[#ec3145] transition-colors cursor-pointer"
                          aria-label="წაშლა"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-[#1C1C1C]/8 bg-[#F5F2EE]">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-[#1C1C1C]/60">ჯამი</span>
              <span className="font-serif text-2xl font-bold text-[#1C1C1C]">
                ₾{totalPrice}
              </span>
            </div>
            <button className="w-full bg-[#ec3145] text-white font-semibold text-sm py-3.5 hover:bg-[#c9283a] transition-colors cursor-pointer whitespace-nowrap">
              შეკვეთის გაფორმება
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="w-full text-center text-xs text-[#1C1C1C]/40 hover:text-[#1C1C1C] mt-3 transition-colors cursor-pointer"
            >
              მაღაზიაში დაბრუნება
            </button>
          </div>
        )}
      </div>
    </>
  );
}